import React from 'react';
import PropTypes from 'prop-types';
import dataModel from '../../models/DataModel';
import ImageTools from '../../helpers/ImageTools';
import LocationProjectsNavCollection from '../../dao/collections/LocationProjectsNavCollection';
import ProjectsList from '../../mobileWrappers/ProjectsListWrapper/ProjectsList';
import MultiFileUploader from '../MultiFileUploader';
import goToButton from '../../assets/images/go-to.svg';
import FileUtil from '../../helpers/FileUtil/FileUtil';

import EmptyPhotoIcon from '../../assets/images/photo-loading.svg';
import TrashIcon from '../../assets/images/trash.svg';
import PhotoPlaceholderIcon from '../../assets/images/photo-jpg-placeholder.svg';
import './MapLocationPopup.scss';
import ProgressIndicator from '../ProgressIndicator';
import Hammer from 'hammerjs';
import anime from 'animejs';
import AnalyticsLogger from '../../helpers/analytics/analyticsLogger';

const locationDescriptor = {
  location: {
    site_photo: {},
    additionalFields: ['not_started_projects', 'in_progress_projects', 'completed_projects', 'site_photo'],
  },
};

const projectDescriptor = {
  project: { 
    additionalFields: ['has_alerts', 'has_unread_alerts', 'has_delays'], 
  },
};
export default class MapLocationPopup extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      projects: null,
      completed_projects: null,
      sitePhoto: props.location.site_photo,
      location: props.location,
      loadingPhoto: false,
    };

    MapLocationPopup.propTypes = {
      location: PropTypes.object.isRequired,
    };

    this.deleteSitePhoto = this.deleteSitePhoto.bind(this);
    this.handleDeleteButton = this.handleDeleteButton.bind(this);
    this.fetchProjects = this.fetchProjects.bind(this);
    this.handleFileSelect = this.handleFileSelect.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleSetComponentElement = this.handleSetComponentElement.bind(this);
    this.handleSwipeEvent = this.handleSwipeEvent.bind(this);
    this.openCoordinates = this.openCoordinates.bind(this);
    this.determineIfLocationHasAlerts = this.determineIfLocationHasAlerts.bind(this);

    const currentRole = dataModel.usersDAO.currentUser ? dataModel.usersDAO.currentUser.role : null;
    this.analyticsLogger = new AnalyticsLogger({
      currentRole,
      isMobile: !!window.cordova,
    });
  }

  handleClickOutside(event) {
    if (!this.state.ignoreClick && this.componentRef && !this.componentRef.contains(event.target)) {
      event.stopPropagation();
      this.props.handleHidePopup();
    }
  }

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside, false);
    delete localStorage.scrollPositionTasks;
    delete localStorage.scrollPositionCompletionStep;
    delete localStorage.scrollPosition;
    dataModel.locationsDAO.getLocation(this.props.location.id,
      { recordDescriptor: { location: { site_photo: {}, additionalFields: ['not_started_projects', 'in_progress_projects', 'completed_projects', 'site_photo'] } } }).then(location => {
        this.setState({ location, sitePhoto: location.site_photo });
        this.fetchProjects(location);
      });
  }

  async handleSwipeEvent(swipeEvent) {
    if (!this.state.expandProjects && swipeEvent.direction == Hammer.DIRECTION_DOWN) {
      await anime({
        targets: '.map-location-popup',
        height: 0,
        easing: 'easeInOutCubic',
        duration: 400,
      }).finished;

      this.setState({ expandProjects: false });
      if (this.props.handleHidePopup) {
        this.props.handleHidePopup();
      }
    }
    else if (!this.state.expandProjects && swipeEvent.direction == Hammer.DIRECTION_UP) {
      const mapElement = document.getElementById(this.props.id);
      if (mapElement) {
        this.saveHeight = mapElement.clientHeight;
      }
      await anime({
        targets: '.map-location-popup',
        height: 495,
        easing: 'easeInOutCubic',
        duration: 200,
      }).finished;

      this.setState({ expandProjects: true });
    }
    else if (this.state.expandProjects && swipeEvent.direction == Hammer.DIRECTION_DOWN) {
      await anime({
        targets: '.map-location-popup',
        height: this.saveHeight ? this.saveHeight : 78,
        easing: 'easeInOutCubic',
        duration: 200,
      }).finished;
      this.setState({ expandProjects: false });
    }
  }

  handleSetComponentElement(element) {
    if (element) {
      this.componentRef = element;
      if (dataModel.mobileBridge.isMobile) {
        const swipeArea = new Hammer(this.componentRef, { touchAction: 'auto' });
        swipeArea.get('pan').set({ direction: Hammer.DIRECTION_ALL });
        const self = this;
        swipeArea.on('panup pandown', swipeEvent => {
          self.handleSwipeEvent(swipeEvent);
        });
      }
    }
  }

  componentWillUnmount() {
    if (this.handleClickOutside) {
      document.removeEventListener('click', this.handleClickOutside, false);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { location } = this.props;
    if (location != prevProps.location) {
      dataModel.locationsDAO.getLocation(this.props.location.id, { recordDescriptor: locationDescriptor })
        .then(location => {
          this.setState({ location, sitePhoto: location.site_photo, loadingPhoto: false });
          this.fetchProjects(location);
        });
    }
    if (location.site_photo != prevProps.location.site_photo) {
      this.setState({ sitePhoto: location.site_photo });
    }
    if (prevProps.ignoreClick != this.props.ignoreClick) {
      this.setState({ ignoreClick: this.props.ignoreClick });
    }
    if (prevState.expandProjects != this.state.expandProjects && this.state.expandProjects) {
      this.analyticsLogger.logMaps('MapSwipeDetails');
    }
  }

  handleDeleteButton() {
    // ensure warning popup doesn't close this
    document.removeEventListener('click', this.handleClickOutside, false);

    dataModel.showGlobalModal('SYSTEM_POPUP', {
      message: 'Are you sure you want to delete this site photo?',
      cancelButtonText: 'Cancel',
      okButtonText: 'Continue',
      height: 184,
      handleOwnerOk: () => {
        document.addEventListener('click', this.handleClickOutside, false);
        this.deleteSitePhoto();
      },
      handleOwnerCancel: () => {
        document.addEventListener('click', this.handleClickOutside, false);
      },
    });
  }

  deleteSitePhoto() {
    const sitePhotoCopy = { ...this.state.sitePhoto };
    dataModel.attachmentsDAO.deleteAttachment(sitePhotoCopy.id)
      .then((result) => {
        const location = { ...this.state.location };
        location.site_photo = null;
        this.setState({ location, sitePhoto: null });
      })
      .catch(error => {
        this.setState({ sitePhoto: sitePhotoCopy });
      });
  }

  handleFileSelect(file) {
    const sitePhotoCopy = { ...this.state.sitePhoto };
    this.setState({ loadingPhoto: true });
    // Preserve rotation data from mobile devices
    ImageTools.resize(file, {}, null, (convertedImage) => {
      const attachment = {
        attachable_type: 'Location',
        attachable_id: this.state.location.id,
        upload: convertedImage,
        name: convertedImage.name,
      };

      return dataModel.attachmentsDAO.createAttachment(attachment)
        .then(sitePhoto => {
          this.setState({ sitePhoto, loadingPhoto: false });
        })
        .catch(error => {
          this.setState({ sitePhoto: sitePhotoCopy, loadingPhoto: false });
        });
    });
  }

  fetchProjects(location) {
    this.locationProjectsNavCollection = LocationProjectsNavCollection.build(location.id, null, null,
      dataModel.usersDAO.currentUser.id, dataModel.companiesDAO.currentCompany.id, location.id + '_forprojectsMap');
    const ids = location.not_started_projects.concat(location.in_progress_projects.concat(location.completed_projects));
    if (ids.length > 0) {
      this.locationProjectsNavCollection.fetchByIds(ids, { clear: true, recordDescriptor: projectDescriptor }).then(projects => {
        let nonCompleted = [];
        let completed = [];
        projects.projects.forEach(project => {
          project.location = location;
          if (project.status != 'complete') {
            nonCompleted.push(project);
          }
          else {
            completed.push(project);
          }
        });
        this.setState({ projects: { projects: nonCompleted }, completed_projects: { projects: completed } }, () => { this.determineIfLocationHasAlerts(); });
      });
    }
    else {
      this.setState({ projects: { projects: [] }, completed_projects: { projects: [] } }, () => { this.determineIfLocationHasAlerts(); });
    }
  }

  openCoordinates() {
    const location = this.state.location;
    const isIos = navigator.userAgent ? /(iphone)/i.test(navigator.userAgent.toLowerCase()) : false;
    const mapsQuery = location.latitude + ',' + location.longitude;

    if (isIos) {
      window.open('maps://?q=' + mapsQuery, '_system');
    }
    else {
      window.open('geo:0,0?q=' + mapsQuery);
    }
  }

  determineIfLocationHasAlerts() {
    const { projects, completed_projects } = this.state;
    const { setLocationsDelay } = this.props;
    const projectsToCheck = [...projects.projects, ...completed_projects];

    if (!projectsToCheck.find(project => project.has_unread_alerts)) {
      if (setLocationsDelay) setLocationsDelay();
    }
  }

  render() {
    const { id } = this.props;
    const { sitePhoto, projects, completed_projects, location, loadingPhoto } = this.state;
    let photoDisplay =
      <MultiFileUploader
        backgroundIcon={PhotoPlaceholderIcon}
        handleFileSelect={this.handleFileSelect}
        acceptedFileTypes={FileUtil.extensionTypes.photoTypes.split(',')}
        maximumAllowedFiles={1}
        currentNumberOfFiles={0}
      />;
    if (loadingPhoto) {
      photoDisplay =
        <div className='loader-wrapper'>
          <img src={EmptyPhotoIcon} alt='uploading...' />
          <ProgressIndicator />
        </div>;
    }
    else if (sitePhoto) {
      let src;
      if (sitePhoto.upload && (sitePhoto.upload instanceof Blob)) {
        src = URL.createObjectURL(sitePhoto.upload);
      }
      else {
        src = sitePhoto.upload;
      }
      photoDisplay =
        <div className='photo-wrapper'>
          <img className='photo' src={src} alt="location" />
          <img className='delete-button' src={TrashIcon} alt='delete' onClick={this.handleDeleteButton} />
        </div>;
    }

    return (
      <div className={'map-location-popup' + (dataModel.mobileBridge.isMobile ? ' mobile' : '') +
        (this.state.expandProjects ? ' mobile-expanded' : '')} id={id} ref={element => { this.handleSetComponentElement(element); }}
      >
        {!dataModel.mobileBridge.isMobile &&
          <div className="map-location-popup__photo">
            {photoDisplay}
          </div>
        }
        <div className={'map-popup-location-header' + (dataModel.mobileBridge.isMobile ? ' dont-wrap-name' : '')} >
          <div className={'map-popup-location-header__name' + (dataModel.mobileBridge.isMobile ? ' dont-wrap-name' : '')}>{location.name}</div>
          <div className="map-popup-location-header__subtext">{'ID: ' + location.guid}</div>
          <div className="map-popup-location-header__subtext lat-lng">{location.latitude + ',' + location.longitude}</div>
          {dataModel.mobileBridge.isMobile &&
            <img
              className="map-go-to"
              alt="go"
              src={goToButton}
              onClick={this.openCoordinates}
            />
          }
        </div>
        {(!dataModel.mobileBridge.isMobile || this.state.expandProjects) &&
          <ProjectsList
            myProjects={projects} completedProjects={completed_projects}
            fromPopup={true}
            expandCompleted={this.props.expandCompleted}
            handleSelectProject={this.props.handleSelectProject}
          />
        }
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/MapLocationPopup/index.js